import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { Layout } from '../components/Layout';
import { FadeInUp, StaggerContainer, StaggerItem } from '../components/AnimatedSection';
import { ExternalLink, Wifi, Sun, Sparkles } from 'lucide-react';

const Evolution = () => {
  const { t } = useLanguage();

  const pillars = [
    {
      key: 'internet',
      icon: Wifi,
      image: 'https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=600&q=80'
    },
    {
      key: 'green',
      icon: Sun,
      image: 'https://images.unsplash.com/photo-1509391366360-2e959784a276?w=600&q=80',
      link: 'https://www.iea.org/reports/renewables-2023'
    },
    {
      key: 'ai',
      icon: Sparkles,
      image: 'https://images.unsplash.com/photo-1677442135703-1787eea5ce01?w=600&q=80',
      link: 'https://www.autodesk.com/solutions/generative-design'
    }
  ];

  return (
    <Layout>
      {/* Hero */}
      <section className="py-24 bg-white" data-testid="evolution-hero">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <FadeInUp>
            <span className="confidential-badge mb-6 inline-block">
              {t('evolution.badge')}
            </span>
          </FadeInUp>
          
          <FadeInUp delay={0.1}>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-navy mb-6" data-testid="evolution-title">
              {t('evolution.title')}
            </h1>
          </FadeInUp>
          
          <FadeInUp delay={0.2}>
            <p className="text-lg md:text-xl text-[#0066CC] font-medium max-w-3xl">
              {t('evolution.subtitle')}
            </p>
          </FadeInUp>
        </div>
      </section>

      {/* Three Pillars */}
      <section className="py-20 bg-slate-50" data-testid="evolution-pillars">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <StaggerContainer className="grid lg:grid-cols-3 gap-8" staggerDelay={0.15}>
            {pillars.map((pillar) => {
              const IconComponent = pillar.icon;
              return (
                <StaggerItem key={pillar.key}>
                  <div 
                    className="bg-white border border-slate-200 overflow-hidden hover:border-[#0066CC] transition-colors group h-full flex flex-col"
                    data-testid={`pillar-${pillar.key}`}
                  >
                    {/* Image */}
                    <div className="relative h-48 overflow-hidden">
                      <img
                        src={pillar.image}
                        alt={t(`evolution.${pillar.key}.title`)}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                      <div className="absolute top-4 left-4">
                        <div className="w-10 h-10 bg-white flex items-center justify-center">
                          <IconComponent className="w-5 h-5 text-[#0066CC]" />
                        </div>
                      </div>
                    </div>
                    
                    {/* Content */}
                    <div className="p-6 flex-1 flex flex-col">
                      <p className="text-xs uppercase tracking-widest text-[#0066CC] font-semibold mb-2">
                        {t(`evolution.${pillar.key}.subtitle`)}
                      </p>
                      <h3 className="text-xl font-bold text-navy mb-4">
                        {t(`evolution.${pillar.key}.title`)}
                      </h3>
                      <p className="text-slate-600 text-sm leading-relaxed flex-1">
                        {t(`evolution.${pillar.key}.text`)}
                      </p>
                      
                      {pillar.link && (
                        <a
                          href={pillar.link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-navy hover:text-[#0066CC] transition-colors mt-4"
                        >
                          {pillar.key === 'green' ? 'IEA Report' : 'Autodesk'}
                          <ExternalLink className="w-3 h-3" />
                        </a>
                      )}
                    </div>
                  </div>
                </StaggerItem>
              );
            })}
          </StaggerContainer>
        </div>
      </section>

      {/* Convergence Section */}
      <section className="py-20 bg-navy text-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <FadeInUp>
            <div className="text-center max-w-3xl mx-auto">
              <h2 className="text-2xl md:text-3xl font-bold mb-6">
                Convergență Tehnologică
              </h2>
              <p className="text-white/80 text-base md:text-lg leading-relaxed mb-12">
                Internetul permite colaborarea globală. Energia verde reduce costurile operaționale. AI-ul optimizează designul. Împreună, aceste tehnologii creează condiții perfecte pentru fabricația aditivă distribuită.
              </p>
              
              <div className="flex justify-center items-center gap-4 flex-wrap">
                <div className="w-20 h-20 border border-white/30 flex items-center justify-center">
                  <Wifi className="w-8 h-8" />
                </div>
                <span className="text-2xl text-white/50">+</span>
                <div className="w-20 h-20 border border-white/30 flex items-center justify-center">
                  <Sun className="w-8 h-8" />
                </div>
                <span className="text-2xl text-white/50">+</span>
                <div className="w-20 h-20 border border-white/30 flex items-center justify-center">
                  <Sparkles className="w-8 h-8" />
                </div>
                <span className="text-2xl text-white/50">=</span>
                <div className="w-20 h-20 bg-[#0066CC] flex items-center justify-center">
                  <span className="text-xs font-bold uppercase tracking-wider">AM</span>
                </div>
              </div>
            </div>
          </FadeInUp>
        </div>
      </section>
    </Layout>
  );
};

export default Evolution;
